import { useCallback, useRef } from 'react'
import * as React from 'react'
import clsx from 'clsx'
import BodyText from 'LuxKit/Typography/BodyText'
import CircleCheckboxInput from 'LuxKit/Checkbox/CircleCheckboxInput'
import Group from 'components/utils/Group'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import IconButton from 'LuxKit/Button/IconButton'
import noop from 'lib/function/noop'
import { SelectableCard } from './SelectableCard'
import { useCardSelection } from './useCardSelection'

export interface CardOption {
  title: string
  description?: string
  emoji?: string
  placeSearch?: string
}

export type CardCategory = 'generic' | 'places' | 'locations'

interface Props {
  options: Array<CardOption>
  onSelectionChange: (selectedTitles: Array<string>) => void
  select: 'single' | 'multiple'
  disabled?: boolean
  selection?: Array<string>
  categories?: CardCategory
  onInfoClick?: (option: CardOption, category: CardCategory) => void
}

function CardsList({ options, onSelectionChange, select, disabled = false, selection, categories, onInfoClick = noop }: Props) {
  const { selectedIndices, optionClickHandlers } = useCardSelection({
    options,
    selection,
    disabled,
    select,
    onSelectionChange,
  })

  const onInfoClickRef = useRef(onInfoClick)
  onInfoClickRef.current = onInfoClick

  const handleInfoClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    const indexStr = event.currentTarget.dataset.optionIndex
    if (indexStr === undefined) return
    const index = parseInt(indexStr, 10)
    if (isNaN(index)) return
    const option = options[index]
    if (option && categories) {
      onInfoClickRef.current(option, categories)
    }
  }, [options, categories])

  if (options.length === 0) {
    return undefined
  }

  return (
    <Group direction="vertical" gap={12} fullWidth>
      {options.map((option, index) => {
        const isSelected = selectedIndices.has(index)
        return (
          <SelectableCard
            key={index}
            role={select === 'single' ? 'radio' : 'checkbox'}
            aria-checked={isSelected}
            disabled={disabled}
            onClick={optionClickHandlers[index]}
            className={clsx({ selected: isSelected })}
          >
            <Group direction="horizontal" gap={16} fullWidth verticalAlign="center">
              {option.emoji && (
                <BodyText variant="large" colour="neutral-one" weight="bold">
                  {option.emoji}
                </BodyText>
              )}
              <Group direction="vertical" gap={2} fullWidth>
                <Group direction="horizontal" gap={4} verticalAlign="center">
                  <BodyText variant="medium" colour="neutral-one" weight="bold">
                    {option.title}
                  </BodyText>
                  {(categories === 'locations' || categories === 'places') && (
                    <IconButton
                      kind="tertiary"
                      size="small"
                      data-option-index={index}
                      onClick={handleInfoClick}
                    >
                      <LineInfoCircleIcon />
                    </IconButton>
                  )}
                </Group>
                {option.description && (
                  <BodyText variant="medium" colour="neutral-two">
                    {option.description}
                  </BodyText>
                )}
              </Group>
              {select === 'multiple' && (
                <CircleCheckboxInput
                  checked={isSelected}
                  disabled={disabled}
                  aria-hidden="true"
                />
              )}
            </Group>
          </SelectableCard>
        )
      })}
    </Group>
  )
}

export default CardsList
